import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocalStorage } from '@utils/utils';
import { FilterSort } from '@hooks/useFilter/Filter.tsx';
import { FilterConfig, SavedFilter, SortableColumn } from './types';
import { LegacyFilterBar } from './LegacyFilterBar';
import { FilterBarV2 } from './v2/FilterBarV2';
import { FilterUiVersion, useFilterUiVersion } from './useFilterUiVersion';
import { useSaveFilterSidebar } from './SaveFilterSidebar';
import ManageFiltersSidebar from './ManageFiltersSidebar';

interface SavedFiltersResponse<TFilter> {
  filters: Array<{
    id: string;
    name: string;
    data: TFilter;
    sort?: {
      sortField: string;
      sortDirection: 'ASC' | 'DESC';
    };
  }>;
}

export interface RingilFilterBarProps<TFilter> {
  filtersConf?: FilterConfig[];
  /** Identity of the filtered view — scopes the per-user "hearted" (default) filter fields. */
  filterName: string;
  filter: TFilter;
  applyFilterValue: (property: string, value: any) => void;
  applyFilter: (filter: TFilter) => void;
  loading?: boolean;
  /** Sorting is optional — a view without sortable columns simply renders no sort chip. */
  sort?: FilterSort;
  applySorting?: (sortField: string, sortDirection: 'ASC' | 'DESC') => void;
  sortableColumns?: SortableColumn[];
  /** Saved filters are optional — without `getSavedFilters` the save/manage controls stay hidden. */
  getSavedFilters?: (onSuccess: (data: SavedFiltersResponse<TFilter>) => void, hashAuthentication?: string, callbacks?: any) => void;
  saveFilters?: (body: { name: string; data: TFilter }, onSuccess: () => void, hashAuthentication?: string, callbacks?: any) => void;
  deleteSavedFilter?: (filterId: string, onSuccess: () => void, hashAuthentication?: string, callbacks?: any) => void;
  /** Applied to the empty filter when resetting, so view-specific defaults survive a clear. */
  normalizeFilter?: (filter: TFilter) => TFilter;
  /**
   * Opt this view into the new (v2) chip UI. Defaults to true; pass false to stay on the legacy grid.
   */
  enableFilterV2?: boolean;
  /**
   * Version state, when the caller renders its own {@link FilterUiVersionSwitch} (RingilDataTable puts
   * the switch below the table). Omit and the bar manages the flag itself.
   */
  filterUiVersion?: FilterUiVersion;
  /** Look of the v2 chip bar — see {@link FilterBarProps.variant}. */
  variant?: 'default' | 'compact';
  /** See {@link FilterBarProps.onOpenAllFilters}. */
  onOpenAllFilters?: () => void;
}

/**
 * The filtering chrome — saved-filter pills, the chip bar (or the legacy grid), the "All filters"
 * sidebar and the save / manage sidebars.
 *
 * Lives outside {@link RingilDataTable} so any view can filter with the same UI: the table renders it
 * above the grid, the timeslot calendar renders it above the calendar. Everything table-specific
 * (columns, paging, mass actions) stays in RingilDataTable; this component only needs a filter object
 * and the `useFilter` callbacks that mutate it.
 */
export const RingilFilterBar = <TFilter,>({
  filtersConf,
  filterName,
  filter,
  applyFilterValue,
  applyFilter,
  loading = false,
  sort,
  applySorting,
  sortableColumns,
  getSavedFilters,
  saveFilters,
  deleteSavedFilter,
  normalizeFilter,
  enableFilterV2 = true,
  filterUiVersion,
  variant,
  onOpenAllFilters,
}: RingilFilterBarProps<TFilter>) => {
  const [savedFilters, setSavedFilters] = useState<SavedFilter<TFilter>[]>([]);
  const [manageFiltersSidebarVisible, setManageFiltersSidebarVisible] = useState(false);
  const [expandedFilters, setExpandedFilters] = useLocalStorage('EXPEDITION_LIST_EXPANDED_FILTERS', false);
  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const ownVersion = useFilterUiVersion();
  const useFilterV2 = enableFilterV2 && (filterUiVersion ?? ownVersion.version) === 'v2';

  const { SaveFilterSidebar, setVisible: setSaveFilterSidebarVisible } = useSaveFilterSidebar<TFilter>();

  const loadSavedFilters = () => {
    if (!getSavedFilters) return;
    getSavedFilters(response => {
      if (response?.filters) {
        setSavedFilters(
          response.filters.map((f: any) => ({
            id: f.id,
            name: f.name,
            data: f.data,
            sort: f.sort,
          })),
        );
      } else {
        setSavedFilters([]);
      }
    });
  };

  useEffect(() => {
    loadSavedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSavedFilters]);

  const clearFilter = () => {
    applyFilter(normalizeFilter ? normalizeFilter({} as TFilter) : ({} as TFilter));
  };

  // Persist an edited saved filter. There's no update API, so emulate it: delete the original, then
  // recreate with the (possibly renamed) name and the edited data.
  const handleSaveEditedFilter = (original: { id: string }, name: string, data: TFilter) => {
    if (!deleteSavedFilter || !saveFilters) return;
    deleteSavedFilter(original.id, () => {
      saveFilters({ name, data }, () => loadSavedFilters());
    });
  };

  const handleDeleteFilter = (filterId: string) => {
    if (!deleteSavedFilter) return;
    deleteSavedFilter(filterId, () => loadSavedFilters());
  };

  const filterBarProps = {
    filtersConf,
    filterName,
    filter,
    applyFilterValue,
    applyFilter,
    savedFilters,
    savingEnabled: !!getSavedFilters,
    loading,
    isMobile,
    expandedFilters,
    setExpandedFilters,
    onSaveFilter: () => setSaveFilterSidebarVisible(true),
    onClearFilter: clearFilter,
    onManageFilters: () => setManageFiltersSidebarVisible(true),
    sort,
    applySorting,
    sortableColumns,
    variant,
    onOpenAllFilters,
  };

  return (
    <>
      {useFilterV2 ? <FilterBarV2 {...filterBarProps} /> : <LegacyFilterBar {...filterBarProps} />}

      <SaveFilterSidebar
        // Persist the RAW filter (same shape as the live filter). Normalization (e.g. pragueStartOfDay
        // on dates) happens once at fetch time — normalizing here too would double-shift dates when the
        // saved filter is later re-applied.
        filter={filter}
        saveFilters={saveFilters}
        onSave={() => loadSavedFilters()}
      />

      <ManageFiltersSidebar
        visible={manageFiltersSidebarVisible}
        onClose={() => setManageFiltersSidebarVisible(false)}
        savedFilters={savedFilters}
        onDelete={handleDeleteFilter}
        filtersConf={filtersConf}
        loading={loading}
        onSaveEdited={handleSaveEditedFilter}
      />
    </>
  );
};

export default RingilFilterBar;
